<script setup>
import { ref, onMounted, computed, reactive } from 'vue';
import { twMerge } from 'tailwind-merge';
import clsx from 'clsx';
import { useRouter } from 'vue-router';
import { toast } from 'vue3-toastify';

const router = useRouter();
const isLoading = ref(true);
const search = ref('');

// User data will be stored here
const users = ref([]);
const me = ref({
    email: ""
})

// Add these variables after other ref declarations
const dialog = ref(false);
const processing = ref(false);
const valid = ref(true);

const editDialog = ref(false);
const editProcessing = ref(false);
const userToEdit = reactive({
    id: null,
    email: '',
    first_name: '',
    last_name: '',
    role: '',
    is_active: false
});

const processingId = ref(null);

const newUser = reactive({
    email: '',
    first_name: '',
    last_name: '',
    role: 'VIEWER'
});

const availableRoles = computed(() => {
    // Partners can only add users with EDITOR and VIEWER roles
    return ['EDITOR', 'VIEWER'];
});

// Computed property to check if current user can add users
const canAddUser = computed(() => {
    return me.value?.role === 'PARTNER';
});

// Method to open dialog for adding new user
const openAddDialog = () => {
    // Reset form
    newUser.email = '';
    newUser.first_name = '';
    newUser.last_name = '';
    newUser.role = 'VIEWER';
    dialog.value = true;
};

// Method to close the dialog
const closeDialog = () => {
    dialog.value = false;
};

// Method to add a new user
const addUser = async () => {
    if (!newUser.email || !newUser.first_name || !newUser.last_name) {
        showSnackbar('Please fill all required fields', 'error');
        return;
    }

    processing.value = true;

    try {
        const response = await fetch(`${import.meta.env['PUBLIC_API_URL']}/partners/users`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
            },
            body: JSON.stringify({
                email: newUser.email,
                first_name: newUser.first_name,
                last_name: newUser.last_name,
                role: newUser.role
            })
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw Error(errorData.message || "Failed to add user");
        }

        showSnackbar('User added successfully');
        closeDialog();
        // Refresh user list
        fetchUsers();
    } catch (error) {
        showSnackbar(`Failed to add user: ${error.message}`, 'error');
        console.error('Error adding user:', error);
    } finally {
        processing.value = false;
    }
};

// Function to determine if a user can be edited
const canEditUser = (user) => {
    // Current partner can edit users with EDITOR or VIEWER roles
    return user.role === 'EDITOR' || user.role === 'VIEWER';
};

// Get available roles for editing based on edited user
const getAvailableEditRoles = () => {
    // Partners can only set EDITOR or VIEWER roles
    return ['EDITOR', 'VIEWER'];
};

// Open edit dialog
const openEditDialog = (user) => {
    userToEdit.id = user.id;
    userToEdit.email = user.email;
    userToEdit.first_name = user.first_name || user.name?.split(' ')[0] || '';
    userToEdit.last_name = user.last_name || user.name?.split(' ').slice(1).join(' ') || '';
    userToEdit.role = user.role;
    userToEdit.is_active = user.is_active || false;

    editDialog.value = true;
};

// Close edit dialog
const closeEditDialog = () => {
    editDialog.value = false;
};

const userToDelete = ref(null);
const deleteDialog = ref(false);

const confirmDelete = (item) => {
    userToDelete.value = item;
    deleteDialog.value = true;
};

const toggleStatus = async (item) => {
    processingId.value = item.id;

    try {
        const response = await fetch(`${import.meta.env['PUBLIC_API_URL']}/partners/users/${item.id}`, {
            method: "PUT",
            body: JSON.stringify({
                is_active: !item.is_active
            }),
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
            },
        });

        if (!response.ok) {
            throw new Error("Failed to update user status");
        }

        item.is_active = !item.is_active;
        showSnackbar(`User ${item.is_active ? 'enabled' : 'disabled'} successfully`, 'success');
    } catch (error) {
        // Revert the toggle if it fails
        showSnackbar('Failed to update User status', 'error');
        console.error('Error toggling User status:', error);
    } finally {
        processingId.value = null;
    }
};

const deleteUser = async () => {
    processing.value = true;
    try {
        const response = await fetch(`${import.meta.env['PUBLIC_API_URL']}/partners/users/${userToDelete.value.id}`, {
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
            },
        });

        if(response.ok) {
            const index = users.value.findIndex(a => a.id === userToDelete.value.id);
            users.value.splice(index, 1);
    
            showSnackbar('User deleted successfully', 'success');
            deleteDialog.value = false;
        } else {
            throw Error("Failed to delete user due to unknown reasons");
        }
    } catch (error) {
        showSnackbar('Failed to delete user', 'error');
        console.error('Error deleting user:', error);
    } finally {
        processing.value = false;
        userToDelete.value = null;
        deleteDialog.value = false;
    }
};

// Update user
const updateUser = async () => {
    if (!userToEdit.email || !userToEdit.first_name || !userToEdit.last_name) {
        showSnackbar('Please fill all required fields', 'error');
        return;
    }
    editProcessing.value = true;

    try {
        const response = await fetch(`${import.meta.env['PUBLIC_API_URL']}/partners/users/${userToEdit.id}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
            },
            body: JSON.stringify({
                first_name: userToEdit.first_name,
                last_name: userToEdit.last_name,
                role: userToEdit.role,
                email: userToEdit.email,
            })
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw Error(errorData.message || "Failed to update user");
        }

        showSnackbar('User updated successfully');
        closeEditDialog();
        fetchUsers();
    } catch (error) {
        showSnackbar(`Failed to update user: ${error.message}`, 'error');
        console.error('Error updating user:', error);
    } finally {
        editProcessing.value = false;
    }
};

// Filtered users based on search term
const filteredUsers = computed(() => {
    if (!search.value) {
        return users.value;
    }

    const searchTerm = search.value.toLowerCase();
    return users.value.filter(user =>
        user.name?.toLowerCase().includes(searchTerm) ||
        user.email?.toLowerCase().includes(searchTerm) ||
        user.role?.toLowerCase().includes(searchTerm)
    );
});

// Methods
const fetchUsers = async () => {
    isLoading.value = true;

    me.value = JSON.parse(localStorage.getItem('user'));
    
    try {
        const response = await fetch(`${import.meta.env['PUBLIC_API_URL']}/partners/users`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
            }
        });

        if (!response.ok) {
            throw Error("Failed to fetch partner users");
        }

        const data = await response.json();
        users.value = data.users || [];

        // Sort users to put current user first
        if (users.value.length > 0 && me.value.email) {
            const currentUserIndex = users.value.findIndex(user => user.email === me.value.email);

            if (currentUserIndex > -1) {
                // Remove the current user from their position and add them to the beginning
                const currentUser = users.value.splice(currentUserIndex, 1)[0];
                users.value.unshift(currentUser);
            }
        }
    } catch (error) {
        showSnackbar('Failed to load partner users', 'error');
        console.error('Error fetching partner users:', error);
    } finally {
        isLoading.value = false;
    }
};

const showSnackbar = (text, color = 'success') => {
    // Using vue3-toastify
    if (color === 'error') {
        toast?.error(text);
    } else {
        toast?.success(text);
    }
};

// Initialize on component mount
onMounted(async () => {
    // Check authentication before loading the page
    const token = localStorage.getItem('auth_token');
    const expiresAt = localStorage.getItem('expiresAt');

    // If no token or token is expired, redirect to login
    if (!token || !expiresAt || new Date(expiresAt).getTime() <= new Date().getTime()) {
        toast?.error("Authentication required");
        router.push('/login');
        return;
    }

    await fetchUsers();
});
</script>

<template>
    <!-- Loading Spinner while loading data -->
    <div v-if="isLoading" class="w-full h-screen flex items-center justify-center bg-page">
        <div class="flex flex-col items-center">
            <div class="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
            <p class="mt-4 text-primary font-monster font-medium">Loading...</p>
        </div>
    </div>

    <!-- Main content - only shown when data is loaded -->
    <div v-else class="w-[calc(100vw-4rem)] flex-grow flex flex-col pl-6 pr-8 py-7 gap-y-8 overflow-y-scroll">
        <div class="p-2 bg-page">
            <h4 class="text-primary font-bold font-monster -ml-2">
                Partner Users Management
            </h4>
        </div>

        <div class="bg-white rounded-[14px] shadow-[2px_2px_6px_rgba(187,195,206,0.4)] p-6">
            <!-- Header with search -->
            <div class="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
                <!-- Search field -->
                <div class="w-full md:w-1/3 relative">
                    <input v-model="search" type="text" placeholder="Search users..."
                        class="rounded-[10px] pl-10 pr-4 py-3 bg-white border-[1px] border-stroke font-monster border-solid outline-none font-medium placeholder:text-[85%] placeholder:text-gray-400 w-full" />
                    <svg class="absolute left-3 top-1/2 -translate-y-1/2" width="18" height="18" viewBox="0 0 24 24"
                        fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                            d="M11 19C15.4183 19 19 15.4183 19 11C19 6.58172 15.4183 3 11 3C6.58172 3 3 6.58172 3 11C3 15.4183 6.58172 19 11 19Z"
                            stroke="#020754" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                        <path d="M21 21L16.65 16.65" stroke="#020754" stroke-width="2" stroke-linecap="round"
                            stroke-linejoin="round" />
                    </svg>
                </div>
                <button v-if="canAddUser" @click="openAddDialog"
                    class="bg-primary hover:bg-primary/90 text-white font-medium py-2 px-4 rounded-lg flex items-center">
                    <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                    </svg>
                    Add User
                </button>
            </div>

            <!-- Add User Dialog -->
            <div v-if="dialog" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div class="bg-white rounded-lg p-6 w-full max-w-md mx-4">
                    <h3 class="text-xl font-bold mb-4 text-secondary">Add New User</h3>

                    <div class="space-y-4">
                        <!-- Email -->
                        <div>
                            <label class="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                            <input v-model="newUser.email" type="email"
                                class="rounded-[10px] px-4 py-3 bg-white border-[1px] border-stroke font-monster border-solid outline-none font-medium w-full"
                                placeholder="Email address" />
                        </div>

                        <!-- First Name -->
                        <div>
                            <label class="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
                            <input v-model="newUser.first_name" type="text"
                                class="rounded-[10px] px-4 py-3 bg-white border-[1px] border-stroke font-monster border-solid outline-none font-medium w-full"
                                placeholder="First name" />
                        </div>

                        <!-- Last Name -->
                        <div>
                            <label class="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
                            <input v-model="newUser.last_name" type="text"
                                class="rounded-[10px] px-4 py-3 bg-white border-[1px] border-stroke font-monster border-solid outline-none font-medium w-full"
                                placeholder="Last name" />
                        </div>

                        <!-- Role -->
                        <div>
                            <label class="block text-sm font-medium text-gray-700 mb-1">Role *</label>
                            <select v-model="newUser.role"
                                class="rounded-[10px] px-4 py-3 bg-white border-[1px] border-stroke font-monster border-solid outline-none font-medium w-full">
                                <option v-for="role in availableRoles" :key="role" :value="role">{{ role }}</option>
                            </select>
                        </div>
                    </div>

                    <div class="mt-6 flex justify-end space-x-3">
                        <button @click="closeDialog"
                            class="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                            :disabled="processing">
                            Cancel
                        </button>
                        <button @click="addUser" class="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
                            :disabled="processing">
                            <span v-if="processing"
                                class="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></span>
                            Add User
                        </button>
                    </div>
                </div>
            </div>

            <!-- Edit User Dialog -->
            <div v-if="editDialog" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div class="bg-white rounded-lg p-6 w-full max-w-md mx-4">
                    <h3 class="text-xl font-bold mb-4 text-secondary">Edit User</h3>

                    <div class="space-y-4">
                        <!-- Email -->
                        <div>
                            <label class="block text-sm font-medium text-gray-700 mb-1">Email Address *</label>
                            <input v-model="userToEdit.email" type="email"
                                class="rounded-[10px] px-4 py-3 border-[1px] border-stroke font-monster border-solid outline-none font-medium w-full"
                                placeholder="Email address" />
                        </div>

                        <!-- First Name -->
                        <div>
                            <label class="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
                            <input v-model="userToEdit.first_name" type="text"
                                class="rounded-[10px] px-4 py-3 border-[1px] border-stroke font-monster border-solid outline-none font-medium w-full"
                                placeholder="First name" />
                        </div>

                        <!-- Last Name -->
                        <div>
                            <label class="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
                            <input v-model="userToEdit.last_name" type="text"
                                class="rounded-[10px] px-4 py-3 border-[1px] border-stroke font-monster border-solid outline-none font-medium w-full"
                                placeholder="Last name" />
                        </div>

                        <!-- Role -->
                        <div>
                            <label class="block text-sm font-medium text-gray-700 mb-1">Role *</label>
                            <select v-model="userToEdit.role"
                                class="rounded-[10px] px-4 py-3 bg-white border-[1px] border-stroke font-monster border-solid outline-none font-medium w-full">
                                <option v-for="role in getAvailableEditRoles()" :key="role" :value="role">{{ role }}</option>
                            </select>
                        </div>
                    </div>

                    <div class="mt-6 flex justify-end space-x-3">
                        <button @click="closeEditDialog"
                            class="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                            :disabled="editProcessing">
                            Cancel
                        </button>
                        <button @click="updateUser"
                            class="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
                            :disabled="editProcessing">
                            <span v-if="editProcessing"
                                class="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></span>
                            Update User
                        </button>
                    </div>
                </div>
            </div>

            <!-- Delete Confirmation Dialog -->
            <div v-if="deleteDialog" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div class="bg-white rounded-lg max-w-md w-full p-6">
                    <h2 class="text-xl font-monster font-bold text-secondary mb-2">Delete User</h2>
                    <p class="mb-6 text-gray-600">
                        Are you sure you want to delete this user? This action cannot be undone.
                    </p>
                    <div v-if="userToDelete" class="mb-6 p-3 bg-gray-50 rounded-lg">
                        <p class="font-monster"><span class="font-bold text-secondary">Email:</span> {{ userToDelete.email }}</p>
                        <p class="font-monster"><span class="font-bold text-secondary">Name:</span> {{ userToDelete.name }}</p>
                    </div>

                    <div class="flex justify-end gap-3">
                        <button @click="deleteDialog = false"
                            class="font-monster py-2 px-4 rounded-[0.625rem] border border-stroke text-secondary hover:bg-gray-50 transition-colors">
                            Cancel
                        </button>
                        <button @click="deleteUser" :disabled="processing"
                            class="font-monster py-2 px-4 rounded-[0.625rem] bg-primary text-white hover:bg-primary/80 transition-colors flex items-center gap-2">
                            <svg v-if="processing" class="animate-spin h-4 w-4 text-white"
                                xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor"
                                    stroke-width="4">
                                </circle>
                                <path class="opacity-75" fill="currentColor"
                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z">
                                </path>
                            </svg>
                            Delete
                        </button>
                    </div>
                </div>
            </div>

            <!-- Users table -->
            <div class="overflow-x-auto">
                <table class="min-w-full divide-y divide-gray-200">
                    <thead>
                        <tr class="font-monster font-bold text-secondary text-left">
                            <th class="py-3 px-4 w-1/4">Name</th>
                            <th class="py-3 px-4 w-2/5">Email</th>
                            <th class="py-3 px-4 w-1/5">Role</th>
                            <th class="py-3 px-4 w-1/5">Status</th>
                            <th class="py-3 px-4 w-1/5">Actions</th>
                        </tr>
                    </thead>
                    <tbody class="bg-white divide-y divide-gray-100">
                        <tr v-for="user in filteredUsers" :key="user.id" class="hover:bg-gray-50">
                            <td class="py-3 px-4 font-monster font-medium">{{ user.name }}</td>
                            <td class="py-3 px-4 font-monster">
                                {{ user.email }} {{ me.email === user.email ? '(You)' : '' }}
                            </td>
                            <td class="py-3 px-4 font-monster">
                                <span :class="user.role === 'PARTNER' ? 'bg-secondary' : 'bg-primary'"
                                    class="text-white text-xs rounded-full px-2 py-1">
                                    {{ user.role }}
                                </span>
                            </td>
                            <td class="py-3 px-4">
                                <div class="w-12 h-6 flex items-center relative cursor-pointer" v-if="canEditUser(user)" 
                                    @click="toggleStatus(user)">
                                    <div class="w-12 h-6 rounded-full transition-colors duration-200 ease-in-out"
                                        :class="user.is_active ? 'bg-secondary' : 'bg-gray-300'">
                                        <div class="w-5 h-5 bg-white rounded-full shadow absolute top-0.5 left-0.5 transition-transform duration-200 ease-in-out"
                                            :class="user.is_active ? 'transform translate-x-6' : ''"
                                            :style="processingId === user.id ? 'opacity: 0.5;' : ''"></div>
                                    </div>
                                </div>
                            </td>
                            <td class="py-3 px-4">
                                <div class="flex items-center justify-center gap-2">
                                    <button v-if="canEditUser(user)" @click="openEditDialog(user)"
                                        class="text-secondary hover:text-primary ml-2" title="Edit user">
                                        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"
                                            xmlns="http://www.w3.org/2000/svg">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z">
                                            </path>
                                        </svg>
                                    </button>
                                    
                                    <button v-if="canEditUser(user)" @click="confirmDelete(user)"
                                        class="text-primary hover:text-red-700 transition-colors duration-200"
                                        :disabled="processingId === user.id">
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
                                            xmlns="http://www.w3.org/2000/svg">
                                            <path d="M3 6H5H21" stroke="currentColor" stroke-width="2"
                                                stroke-linecap="round" stroke-linejoin="round" />
                                            <path
                                                d="M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z"
                                                stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                                stroke-linejoin="round" />
                                        </svg>
                                    </button>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div v-if="users.length === 0 && !isLoading" class="text-center py-10">
                <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
                <p class="mt-2 text-sm text-gray-500">No partner users found</p>
            </div>
        </div>
    </div>
</template>
